import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Wallet, History, Pencil, UserX, UserCheck, Trash2 } from "lucide-react";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const EMPTY = { project_id: "", name: "", role: "", phone: "", monthly_salary: "", joining_date: "", notes: "" };
const METHODS = [["cash", "Cash"], ["upi", "UPI"], ["bank_transfer", "Bank transfer"], ["cheque", "Cheque"]];
const localISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString(); };
const thisMonth = () => localISO().slice(0, 7);
const today = () => localISO().slice(0, 10);
const methodLabel = (m) => (METHODS.find(([v]) => v === m) || [m, m])[1];
const th = "px-4 py-3 font-heading text-xs uppercase tracking-wider";

function EmployeeForm({ form, setForm, projects, showProject }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3 py-2">
      {showProject && (
        <div className="col-span-2">
          <Label>Project</Label>
          <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
            <SelectTrigger data-testid="emp-project"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div><Label>Name</Label><Input data-testid="emp-name" value={form.name} onChange={set("name")} /></div>
      <div><Label>Role</Label><Input data-testid="emp-role" value={form.role} onChange={set("role")} placeholder="e.g. Supervisor" /></div>
      <div><Label>Phone</Label><Input data-testid="emp-phone" inputMode="tel" value={form.phone} onChange={set("phone")} /></div>
      <div><Label>Monthly salary (₹)</Label><Input data-testid="emp-salary" type="number" min="0" step="1" value={form.monthly_salary} onChange={set("monthly_salary")} /></div>
      <div><Label>Joining date</Label><Input data-testid="emp-joining" type="date" value={form.joining_date || ""} onChange={set("joining_date")} /></div>
      <div className="col-span-2"><Label>Notes</Label><Input data-testid="emp-notes" value={form.notes || ""} onChange={set("notes")} /></div>
    </div>
  );
}

function PayDialog({ emp, onClose, onDone }) {
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (emp) { setPay({ kind: "salary", amount: emp.monthly_salary || "", method: "bank_transfer", paid_date: today(), period: thisMonth(), reference_no: "", notes: "" }); }
  }, [emp]);
  if (!emp || !pay) return null;
  const set = (k) => (e) => setPay({ ...pay, [k]: e.target.value });
  const submit = async () => {
    if (!(Number(pay.amount) > 0)) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${emp.id}/payments`, { ...pay, amount: Number(pay.amount), period: pay.kind === "salary" ? pay.period : null });
      toast.success(`${pay.kind === "salary" ? "Salary" : "Advance"} of ${fmtINR(pay.amount)} recorded for ${emp.name}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="emp-pay-dialog">
        <DialogHeader>
          <DialogTitle>Pay {emp.name}</DialogTitle>
          <DialogDescription>Recorded as a paid project expense for {emp.project_name}.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {[["salary", "Salary"], ["advance", "Advance"]].map(([k, l]) => (
            <Button key={k} type="button" data-testid={`emp-pay-kind-${k}`} variant={pay.kind === k ? "default" : "outline"}
              onClick={() => setPay({ ...pay, kind: k, amount: k === "salary" ? (emp.monthly_salary || "") : "" })}>{l}</Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 py-1">
          <div><Label>Amount (₹)</Label><Input data-testid="emp-pay-amount" type="number" min="0" step="0.01" value={pay.amount} onChange={set("amount")} /></div>
          {pay.kind === "salary"
            ? <div><Label>For month</Label><Input data-testid="emp-pay-period" type="month" value={pay.period} onChange={set("period")} /></div>
            : <div />}
          <div>
            <Label>Method</Label>
            <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
              <SelectTrigger data-testid="emp-pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Paid on</Label><Input data-testid="emp-pay-date" type="date" value={pay.paid_date} onChange={set("paid_date")} /></div>
          <div className="col-span-2"><Label>Reference / notes</Label><Input data-testid="emp-pay-notes" value={pay.notes} onChange={set("notes")} /></div>
        </div>
        <DialogFooter><Button data-testid="emp-pay-submit" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Record payment"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistorySheet({ emp, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { setRows((await api.get(`/employees/${emp.id}/payments`)).data); }
    catch (e) { toast.error(errMsg(e, "Couldn't load payments")); }
  };
  useEffect(() => { if (emp) { load(); } /* eslint-disable-next-line */ }, [emp]);
  if (!emp) return null;
  const remove = async (p) => {
    if (!window.confirm(`Delete this ${p.kind} of ${fmtINR(p.amount)}? Its expense entry is removed too.`)) return;
    try { await api.delete(`/employee-payments/${p.id}`); toast.success("Payment deleted"); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="emp-history-sheet">
        <SheetHeader><SheetTitle>{emp.name}</SheetTitle><SheetDescription>{emp.role || "Staff"} · {emp.project_name}</SheetDescription></SheetHeader>
        <div className="mt-6 space-y-2">
          {rows.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No payments yet.</div>}
          {rows.map((p) => (
            <div key={p.id} data-testid={`emp-pay-row-${p.id}`} className="border border-border rounded-md p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{p.kind === "salary" ? `Salary · ${p.period}` : "Advance"}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(p.paid_date)} · {methodLabel(p.method)}{p.notes ? ` · ${p.notes}` : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                <span className="tabular font-heading font-bold">{fmtINR(p.amount)}</span>
                <Button data-testid={`emp-pay-delete-${p.id}`} size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Employees() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [histFor, setHistFor] = useState(null);

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (projectFilter !== "all") params.project_id = projectFilter;
    if (showInactive) params.include_inactive = true;
    try {
      const [e, p] = await Promise.all([api.get("/employees", { params }), api.get("/projects")]);
      setList(e.data); setProjects(p.data);
    } catch (err) { toast.error(errMsg(err, "Couldn't load employees")); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, projectFilter, showInactive]);

  const save = async () => {
    if (!editFor && !form.project_id) { toast.error("Select a project"); return; }
    if (!form.name.trim()) { toast.error("Enter the employee name"); return; }
    const body = { ...form, monthly_salary: Number(form.monthly_salary || 0) };
    try {
      if (editFor) { delete body.project_id; await api.patch(`/employees/${editFor.id}`, body); toast.success("Employee updated"); }
      else { await api.post("/employees", body); toast.success("Employee added"); }
      setForm(null); setEditFor(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openEdit = (e) => { setEditFor(e); setForm({ name: e.name, role: e.role || "", phone: e.phone || "", monthly_salary: e.monthly_salary ?? "", joining_date: e.joining_date || "", notes: e.notes || "" }); };
  const setActive = async (e, active) => {
    try { await api.patch(`/employees/${e.id}`, { active }); toast.success(active ? `${e.name} reactivated` : `${e.name} marked inactive`); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const active = list.filter((e) => e.active !== false);
  const payroll = active.reduce((s, e) => s + Number(e.monthly_salary || 0), 0);
  const paidMonth = list.reduce((s, e) => s + Number(e.salary_this_month || 0), 0);

  return (
    <div>
      <PageHeader title="Employees" subtitle="Staff per project. Salary and advance payments are recorded as paid project expenses."
        actions={
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
              <Input data-testid="emp-search" placeholder="Search name / role / phone" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-64" />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger data-testid="emp-project-filter" className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 cursor-pointer">
              <input data-testid="emp-show-inactive" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
            </label>
            <Button data-testid="new-employee-btn" className="fab-m" onClick={() => { setEditFor(null); setForm(EMPTY); }}><Plus className="h-4 w-4 mr-2" />New employee</Button>
          </div>
        } />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-md:gap-3 mb-6">
        <StatCard label="Active staff" value={active.length} testId="emp-stat-count" />
        <StatCard label="Monthly payroll" value={fmtINR(payroll)} testId="emp-stat-payroll" />
        <div className="max-md:col-span-2"><StatCard label="Salary paid this month" value={fmtINR(paidMonth)} tone="good" testId="emp-stat-paid" /></div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className={th}>Name</th><th className={th}>Project</th><th className={th}>Role</th><th className={th}>Phone</th>
            <th className={`${th} text-right`}>Monthly salary</th><th className={`${th} text-right`}>Paid this month</th>
            <th className={`${th} text-right`}>Advances</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {list.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.id}`} className={`border-t border-border hover:bg-muted/40 ${e.active === false ? "bg-stone-50 text-stone-500" : ""}`}>
                <td className="px-4 py-3 font-medium">{e.name}{e.active === false && <span className="ml-2 text-[10px] uppercase tracking-widest rounded border border-border px-1.5 py-0.5 text-muted-foreground">Inactive</span>}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.project_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.role || "-"}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.phone ? <a href={`tel:${e.phone}`} className="hover:underline">{e.phone}</a> : "-"}</td>
                <td className="px-4 py-3 text-right tabular">{fmtINR(e.monthly_salary || 0)}</td>
                <td className="px-4 py-3 text-right tabular text-primary">{fmtINR(e.salary_this_month)}</td>
                <td className="px-4 py-3 text-right tabular">{fmtINR(e.advances_total)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {e.active !== false && <Button data-testid={`emp-pay-${e.id}`} size="sm" variant="outline" onClick={() => setPayFor(e)}><Wallet className="h-3.5 w-3.5 mr-1" />Pay</Button>}
                    <Button data-testid={`emp-history-${e.id}`} size="sm" variant="ghost" title="Payment history" onClick={() => setHistFor(e)}><History className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`emp-edit-${e.id}`} size="sm" variant="ghost" title="Edit" onClick={() => openEdit(e)}><Pencil className="h-3.5 w-3.5" /></Button>
                    {e.active !== false
                      ? <Button data-testid={`emp-deactivate-${e.id}`} size="sm" variant="ghost" title="Mark inactive" onClick={() => setActive(e, false)}><UserX className="h-3.5 w-3.5" /></Button>
                      : <Button data-testid={`emp-reactivate-${e.id}`} size="sm" variant="ghost" title="Reactivate" onClick={() => setActive(e, true)}><UserCheck className="h-3.5 w-3.5" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No employees yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(v) => { if (!v) { setForm(null); setEditFor(null); } }}>
        <DialogContent className="max-w-xl" data-testid="emp-form-dialog">
          <DialogHeader><DialogTitle>{editFor ? `Edit ${editFor.name}` : "New employee"}</DialogTitle></DialogHeader>
          {form && <EmployeeForm form={form} setForm={setForm} projects={projects} showProject={!editFor} />}
          <DialogFooter><Button data-testid="emp-save-btn" onClick={save}>{editFor ? "Save changes" : "Add employee"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <PayDialog emp={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />
      <HistorySheet emp={histFor} onClose={() => setHistFor(null)} onChanged={load} />
    </div>
  );
}
